/**
 * Lazy toast — dynamically imports sonner on first call so ~15 KB of
 * Toaster styles/runtime stays out of the LCP-critical main bundle.
 *
 * Use this in pages that are part of main.js (e.g. Landing.jsx) where
 * toast() is only fired after user interaction. Lazy-loaded pages can
 * keep using `import { toast } from "sonner"` directly — they don't
 * affect the LCP path.
 *
 * Same public API as sonner's `toast` — supports .success / .error /
 * .info / .warning / plain call.
 */

let _sonnerToast = null;
let _pendingLoad = null;

async function ensureSonner() {
  if (_sonnerToast) return _sonnerToast;
  if (!_pendingLoad) {
    _pendingLoad = import(/* webpackChunkName: "sonner-runtime" */ "sonner").then((m) => {
      _sonnerToast = m.toast;
      return _sonnerToast;
    });
  }
  return _pendingLoad;
}

function fire(method, ...args) {
  ensureSonner().then((t) => {
    if (method) t[method](...args);
    else t(...args);
  });
}

// Stable-id support so the loading → success/error/dismiss update pattern
// (e.g. walletCheckout) works even though sonner loads asynchronously.
let _seq = 0;
function fireWithId(method, args) {
  let opts = args[1];
  if (!opts || typeof opts !== "object") {
    opts = {};
    args = [args[0], opts];
  }
  if (opts.id == null) opts.id = `vk-${++_seq}`;
  fire(method, ...args);
  return opts.id;
}

// Public API — mirrors `toast` shape from sonner.
function toast(...args)         { fire(null, ...args); }
toast.success = (...args)       => fire("success", ...args);
toast.error   = (...args)       => fire("error",   ...args);
toast.info    = (...args)       => fire("info",    ...args);
toast.warning = (...args)       => fire("warning", ...args);
toast.message = (...args)       => fire("message", ...args);
toast.loading = (...args)       => fireWithId("loading", args);
toast.dismiss = (...args)       => fire("dismiss", ...args);

export { toast };
